<template lang="pug">
.password-input
  el-popover(
    v-model:visible="showHelp",
    trigger="manual",
    placement="top",
    :width="400"
  )
    template(#reference)
      el-input(
        ref="inputRef",
        v-model="val",
        :id="id",
        type="password",
        :placeholder="placeholder",
        :aria-describedby="ariaDescribedby",
        :aria-invalid="ariaInvalid",
        :aria-required="ariaRequired",
        :disabled="disabled",
        show-password,
        @focus="showHelp = true",
        @blur="showHelp = false",
        @keyup.enter="enter"
      )
    ul.help-message
      li.require.mb-1
        i.icon.icon--success.material-icons(v-if="correctLength") done
        i.icon.material-icons(v-else) clear
        span.ml-1 {{ $t("minimumLength") }}
      li.require.mb-1
        i.icon.icon--success.material-icons(v-if="capitalLetter") done
        i.icon.material-icons(v-else) clear
        span.ml-1 {{ $t("atLeastOneBigAndSmall") }}
      li.require
        i.icon.icon--success.material-icons(v-if="!isBlocked") done
        i.icon.material-icons(v-else) clear
        span.ml-1 {{ $t("passwordNotEqualUsernameAndEmail") }}
</template>
<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { getSetting } from "@/getSettings";

interface Props {
  modelValue?: string;
  blocked?: string[];
  placeholder?: string;
  id?: string;
  ariaDescribedby?: string;
  ariaInvalid?: boolean;
  ariaRequired?: boolean;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  blocked: () => [],
  placeholder: "placeholder",
  id: undefined,
  ariaDescribedby: undefined,
  ariaInvalid: false,
  ariaRequired: false,
  disabled: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: string];
  enter: [];
}>();

const showHelp = ref(false);
const val = ref(props.modelValue || "");
const inputRef = ref();

const correctLength = computed(() => val.value.length >= 8);

const capitalLetter = computed(() => {
  // NOTE: we also allow customer specific regex
  if (getSetting("passwordRegex")) {
    const regex = new RegExp(getSetting("passwordRegex"));
    return regex.test(val.value);
  }

  const oldString = val.value;
  const lowerCase = oldString.toLowerCase();
  const upperCase = oldString.toUpperCase();
  return oldString !== lowerCase && oldString !== upperCase;
});

const isBlocked = computed(() => {
  return props.blocked.indexOf(val.value) !== -1;
});

const isValid = computed(() => {
  return correctLength.value && capitalLetter.value && !isBlocked.value;
});

watch(val, (vl) => {
  const value = vl.trim();
  if (value.length === 0) {
    emit("update:modelValue", "");
  } else if (isValid.value) {
    emit("update:modelValue", value);
  }
});

watch(correctLength, () => {
  if (isValid.value) {
    emit("update:modelValue", val.value);
  } else {
    emit("update:modelValue", "");
  }
});

watch(capitalLetter, () => {
  if (isValid.value) {
    emit("update:modelValue", val.value);
  } else {
    emit("update:modelValue", "");
  }
});

watch(isBlocked, () => {
  if (isValid.value) {
    emit("update:modelValue", val.value);
  } else {
    emit("update:modelValue", "");
  }
});

// Watch the actual focus state of the input element
watch(inputRef, (input) => {
  if (input && input.input) {
    const inputElement = input.input as HTMLInputElement;

    const checkFocus = () => {
      showHelp.value = document.activeElement === inputElement;
      if (document.activeElement === inputElement) {
        // Re-check in a bit to maintain focus state
        setTimeout(checkFocus, 200);
      }
    };

    inputElement.addEventListener("focus", checkFocus);
    inputElement.addEventListener("blur", () => {
      showHelp.value = false;
    });
  }
});

const enter = () => {
  emit("enter");
};
</script>

<style scoped>
.require {
  display: flex;
  align-items: center;
}

.indicator {
  height: 4px;
}

.help-message {
  margin-bottom: 15px;
  padding-left: 8px;
}

.help-message .icon {
  font-size: 16px;
  color: #f56c6c;
}

.help-message .icon--success {
  color: #67c23a;
}
</style>
